// ============================================================================
// Constants
// ============================================================================
/**
 * Mapping from data-sf-type values to HtmlStepperFieldType
 */
const SF_TYPE_TO_FIELD_TYPE = {
    text: 'text',
    email: 'email',
    tel: 'tel',
    phone: 'phone',
    number: 'number',
    textarea: 'textarea',
    textArea: 'textarea', // Normalize to lowercase
    date: 'date',
    select: 'select',
    multiChoice: 'multiChoice',
    checkbox: 'checkbox',
    range: 'range',
    country: 'country',
    state: 'state',
    address: 'address',
    hidden: 'hidden',
};
// ============================================================================
// Core Extraction Logic
// ============================================================================
/**
 * Helper to extract options from an element
 */
function extractOptions(element) {
    const optionElements = element.querySelectorAll('[data-sf-option]');
    return optionElements.map((opt) => {
        const value = opt.getAttribute('data-sf-option') || '';
        const label = opt.getAttribute('data-sf-label') || opt.textContent?.trim() || undefined;
        // Only include label if it exists
        return label ? { value, label } : { value };
    });
}
/**
 * Extract form fields from a pre-parsed HTML root.
 * Works with any parser that implements the ParsedHtml interface.
 *
 * @param root - The parsed HTML root element
 * @returns Array of extracted HtmlStepperField objects
 */
function extractFieldsFromParsedHtml(root) {
    const fields = [];
    const seenIds = new Set();
    // Find all elements with data-sf-field attribute
    const fieldElements = root.querySelectorAll('[data-sf-field]');
    for (const element of fieldElements) {
        const fieldId = element.getAttribute('data-sf-field');
        if (!fieldId || seenIds.has(fieldId)) {
            continue;
        }
        seenIds.add(fieldId);
        const sfType = element.getAttribute('data-sf-type') || 'text';
        const fieldType = SF_TYPE_TO_FIELD_TYPE[sfType] || 'text';
        const required = element.getAttribute('data-sf-required') === 'true';
        const protected_ = element.getAttribute('data-sf-protected') === 'true';
        const defaultValue = element.getAttribute('data-sf-default-value') || undefined;
        // Build field based on type - only include properties with values
        let field;
        switch (fieldType) {
            case 'text':
            case 'email':
            case 'tel':
            case 'textarea': {
                const pattern = element.getAttribute('data-sf-pattern') || undefined;
                field = {
                    id: fieldId,
                    type: fieldType,
                    required,
                    ...(protected_ && { protected: protected_ }),
                    ...(defaultValue && { defaultValue }),
                    ...(pattern && { pattern }),
                };
                break;
            }
            case 'number': {
                const minStr = element.getAttribute('data-sf-min-value');
                const maxStr = element.getAttribute('data-sf-max-value');
                const minValue = minStr ? parseFloat(minStr) : undefined;
                const maxValue = maxStr ? parseFloat(maxStr) : undefined;
                field = {
                    id: fieldId,
                    type: 'number',
                    required,
                    ...(protected_ && { protected: protected_ }),
                    ...(defaultValue && { defaultValue }),
                    ...(minValue !== undefined && { minValue }),
                    ...(maxValue !== undefined && { maxValue }),
                };
                break;
            }
            case 'range': {
                const minStr = element.getAttribute('data-sf-min-value');
                const maxStr = element.getAttribute('data-sf-max-value');
                const stepStr = element.getAttribute('data-sf-step');
                const minValue = minStr ? parseFloat(minStr) : undefined;
                const maxValue = maxStr ? parseFloat(maxStr) : undefined;
                const step = stepStr ? parseFloat(stepStr) : undefined;
                field = {
                    id: fieldId,
                    type: 'range',
                    required,
                    ...(protected_ && { protected: protected_ }),
                    ...(defaultValue && { defaultValue }),
                    ...(minValue !== undefined && { minValue }),
                    ...(maxValue !== undefined && { maxValue }),
                    ...(step !== undefined && { step }),
                };
                break;
            }
            case 'date': {
                const minDate = element.getAttribute('data-sf-min-date') || undefined;
                const maxDate = element.getAttribute('data-sf-max-date') || undefined;
                field = {
                    id: fieldId,
                    type: 'date',
                    required,
                    ...(protected_ && { protected: protected_ }),
                    ...(defaultValue && { defaultValue }),
                    ...(minDate && { minDate }),
                    ...(maxDate && { maxDate }),
                };
                break;
            }
            case 'select': {
                const options = extractOptions(element);
                field = {
                    id: fieldId,
                    type: 'select',
                    required,
                    ...(protected_ && { protected: protected_ }),
                    ...(defaultValue && { defaultValue }),
                    ...(options.length > 0 && { options }),
                };
                break;
            }
            case 'multiChoice': {
                const multiple = element.getAttribute('data-sf-multiple') === 'true';
                const autoAdvance = element.getAttribute('data-sf-auto-advance') === 'true';
                const minStr = element.getAttribute('data-sf-min');
                const maxStr = element.getAttribute('data-sf-max');
                const min = minStr ? parseInt(minStr, 10) : undefined;
                const max = maxStr ? parseInt(maxStr, 10) : undefined;
                const options = extractOptions(element);
                field = {
                    id: fieldId,
                    type: 'multiChoice',
                    required,
                    ...(protected_ && { protected: protected_ }),
                    ...(defaultValue && { defaultValue }),
                    ...(multiple && { multiple }),
                    ...(!multiple && autoAdvance && { autoAdvance }),
                    ...(min !== undefined && { min }),
                    ...(max !== undefined && { max }),
                    ...(options.length > 0 && { options }),
                };
                break;
            }
            case 'checkbox': {
                field = {
                    id: fieldId,
                    type: 'checkbox',
                    required,
                    ...(protected_ && { protected: protected_ }),
                    ...(defaultValue && { defaultValue }),
                };
                break;
            }
            case 'country':
            case 'state':
            case 'phone': {
                field = {
                    id: fieldId,
                    type: fieldType,
                    required,
                    ...(protected_ && { protected: protected_ }),
                    ...(defaultValue && { defaultValue }),
                };
                break;
            }
            case 'address': {
                // Parse restrict countries (comma-separated ISO codes)
                const restrictCountriesStr = element.getAttribute('data-sf-restrict-countries');
                const restrictCountries = restrictCountriesStr
                    ? restrictCountriesStr
                        .split(',')
                        .map((c) => c.trim().toUpperCase())
                        .filter(Boolean)
                    : undefined;
                // Parse place types (comma-separated)
                const placeTypesStr = element.getAttribute('data-sf-place-types');
                const placeTypes = placeTypesStr
                    ? placeTypesStr
                        .split(',')
                        .map((t) => t.trim())
                        .filter(Boolean)
                    : undefined;
                // Boolean options
                const editableAfterSelection = element.getAttribute('data-sf-editable-after-selection') === 'true';
                const allowManualEntry = element.getAttribute('data-sf-allow-manual-entry') === 'true';
                // Language
                const language = element.getAttribute('data-sf-language') || undefined;
                // Parse component config from individual attributes
                const componentConfig = {};
                const componentKeys = [
                    'streetNumber',
                    'route',
                    'secondaryLine',
                    'locality',
                    'administrativeAreaLevel1',
                    'postalCode',
                    'country',
                ];
                for (const key of componentKeys) {
                    // Convert camelCase to kebab-case for attribute name
                    const attrName = `data-sf-component-${key
                        .replace(/([A-Z])/g, '-$1')
                        .toLowerCase()}`;
                    const value = element.getAttribute(attrName);
                    if (value && ['hidden', 'optional', 'required'].includes(value)) {
                        componentConfig[key] = value;
                    }
                }
                const hasComponentConfig = Object.keys(componentConfig).length > 0;
                field = {
                    id: fieldId,
                    type: 'address',
                    required,
                    ...(protected_ && { protected: protected_ }),
                    ...(defaultValue && { defaultValue }),
                    ...(restrictCountries &&
                        restrictCountries.length > 0 && { restrictCountries }),
                    ...(placeTypes && placeTypes.length > 0 && { placeTypes }),
                    ...(editableAfterSelection && { editableAfterSelection }),
                    ...(hasComponentConfig && { componentConfig }),
                    ...(allowManualEntry && { allowManualEntry }),
                    ...(language && { language }),
                };
                break;
            }
            case 'hidden': {
                field = {
                    id: fieldId,
                    type: 'hidden',
                    required: false,
                    ...(protected_ && { protected: protected_ }),
                    ...(defaultValue && { defaultValue }),
                };
                break;
            }
            default: {
                // Fallback to text field
                field = {
                    id: fieldId,
                    type: 'text',
                    required,
                    ...(protected_ && { protected: protected_ }),
                    ...(defaultValue && { defaultValue }),
                };
            }
        }
        fields.push(field);
    }
    return fields;
}
// ============================================================================
// Browser-specific Implementation
// ============================================================================
/**
 * Wrap a native DOM Element to match our FieldElement interface.
 */
function wrapDomElement(element) {
    return {
        getAttribute: (name) => element.getAttribute(name),
        querySelectorAll: (selector) => Array.from(element.querySelectorAll(selector)).map(wrapDomElement),
        textContent: element.textContent,
    };
}
/**
 * Extract form fields from HTML string using browser's native DOMParser.
 * Only works in browser environment.
 *
 * @param html - The HTML content to parse
 * @returns Array of extracted HtmlStepperField objects
 */
function extractFieldsFromHtmlBrowser(html) {
    if (!html || html.trim() === '') {
        return [];
    }
    if (typeof DOMParser === 'undefined') {
        throw new Error('extractFieldsFromHtmlBrowser requires a browser environment. ' +
            'Use extractFieldsFromParsedHtml with node-html-parser for Node.js.');
    }
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    // Wrap native DOM to match our interface
    const root = {
        querySelectorAll: (selector) => Array.from(doc.querySelectorAll(selector)).map(wrapDomElement),
    };
    return extractFieldsFromParsedHtml(root);
}
/**
 * Validate HTML structure for common issues.
 * Works with pre-parsed HTML.
 *
 * @param root - The parsed HTML root element
 * @returns Validation result with errors and warnings
 */
function validateStepHtmlFromParsed(root) {
    const errors = [];
    const warnings = [];
    // Check for duplicate field IDs
    const fieldIds = new Set();
    const fieldElements = root.querySelectorAll('[data-sf-field]');
    for (const el of fieldElements) {
        const id = el.getAttribute('data-sf-field');
        if (id) {
            if (fieldIds.has(id)) {
                errors.push(`Duplicate field ID: ${id}`);
            }
            fieldIds.add(id);
        }
    }
    // Check for fields without type
    for (const el of fieldElements) {
        const id = el.getAttribute('data-sf-field');
        const type = el.getAttribute('data-sf-type');
        if (!type) {
            warnings.push(`Field "${id}" has no data-sf-type attribute, defaulting to "text"`);
        }
    }
    // Check for multiChoice without options
    const multiChoiceElements = root.querySelectorAll('[data-sf-type="multiChoice"]');
    for (const el of multiChoiceElements) {
        const options = el.querySelectorAll('[data-sf-option]');
        if (options.length === 0) {
            const id = el.getAttribute('data-sf-field');
            warnings.push(`multiChoice field "${id}" has no options defined`);
        }
    }
    return {
        isValid: errors.length === 0,
        errors,
        warnings,
    };
}
/**
 * Validate HTML string for common issues (browser only).
 *
 * @param html - The HTML content to validate
 * @returns Validation result with errors and warnings
 */
function validateStepHtmlBrowser(html) {
    if (!html || html.trim() === '') {
        return { isValid: true, errors: [], warnings: [] };
    }
    if (typeof DOMParser === 'undefined') {
        throw new Error('validateStepHtmlBrowser requires a browser environment.');
    }
    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const root = {
            querySelectorAll: (selector) => Array.from(doc.querySelectorAll(selector)).map(wrapDomElement),
        };
        return validateStepHtmlFromParsed(root);
    }
    catch (error) {
        return {
            isValid: false,
            errors: [
                `Failed to parse HTML: ${error instanceof Error ? error.message : 'Unknown error'}`,
            ],
            warnings: [],
        };
    }
}
// ============================================================================
// Utilities
// ============================================================================
/**
 * Strip CDATA wrappers from HTML content.
 * LLMs sometimes wrap HTML in <![CDATA[...]]> tags which need to be removed.
 *
 * @param html - The HTML content that may contain CDATA wrappers
 * @returns The HTML content with CDATA wrappers stripped
 */
function stripCdataWrappers(html) {
    if (!html)
        return html;
    let result = html;
    // Remove CDATA opening tags
    result = result.replace(/<!\[CDATA\[/gi, '');
    // Remove CDATA closing tags
    result = result.replace(/\]\]>/g, '');
    return result.trim();
}

export { extractFieldsFromHtmlBrowser, extractFieldsFromParsedHtml, stripCdataWrappers, validateStepHtmlBrowser, validateStepHtmlFromParsed };
//# sourceMappingURL=html-stepper-field-extractor.esm.js.map
